import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { SmartStatementBucketSetting } from '@anduin/api';
import { mapToAndResult } from '@anduin/shared';
import { BehaviorSubject, Subject, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { CollectionsSettingsStep, CollectionsTrackService, SettingsMessageEditorOpenedMetadata } from '../../tracking';

@Component({
  selector: 'app-collections-message-form',
  templateUrl: './collections-message-form.component.html',
  styleUrls: ['./collections-message-form.component.scss'],
  standalone: false,
})
export class CollectionsMessageFormComponent implements OnInit, OnDestroy {
  private _unsubscribe: Subject<void> = new Subject<void>();

  subjectControl: UntypedFormControl = this.builder.control('');
  headlineControl: UntypedFormControl = this.builder.control('');
  messageControl: UntypedFormControl = this.builder.control('');

  isSubjectValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isHeadlineValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isMessageValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  @Input()
  set subject(newSubject: string) {
    this.subjectControl.setValue(newSubject, { emitEvent: false });
  }

  @Input()
  set headline(newHeadline: string) {
    this.headlineControl.setValue(newHeadline, { emitEvent: false });
  }

  @Input()
  set message(newMessage: string) {
    this.messageControl.setValue(newMessage, { emitEvent: false });
  }

  @Input() messageVariables: Map<string, string> = new Map<string, string>();

  @Output()
  subjectChange: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  headlineChange: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  messageChange: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _editorEngagementTracked = false;

  constructor(
    private readonly builder: UntypedFormBuilder,
    private readonly trackService: CollectionsTrackService,
  ) {}

  ngOnInit() {
    merge(
      this.subjectControl.valueChanges.pipe(tap((value) => this.handleEditorChange('subject', value))),
      this.headlineControl.valueChanges.pipe(tap((value) => this.handleEditorChange('headline', value))),
      this.messageControl.valueChanges.pipe(tap((value) => this.handleEditorChange('message', value))),
    )
      .pipe(takeUntil(this._unsubscribe))
      .subscribe();

    combineLatest([this.isSubjectValid$, this.isHeadlineValid$, this.isMessageValid$])
      .pipe(
        mapToAndResult(),
        distinctUntilChanged(),
        tap((state) => this.isValid.emit(state)),
        takeUntil(this._unsubscribe),
      )
      .subscribe();
  }

  reset(zeroToThirtyBucketSettings: SmartStatementBucketSetting): void {
    this.subject = zeroToThirtyBucketSettings.subject;
    this.headline = zeroToThirtyBucketSettings.header;
    this.message = zeroToThirtyBucketSettings.body;
  }

  /**
   * Edits flow through `valueChanges`; programmatic `setValue` calls pass `emitEvent: false`, so any
   * emission here is user-initiated. Fire `SETTINGS_MESSAGE_EDITOR_OPENED` once per component instance
   * to measure whether firms actually engage with the template vs accept defaults.
   */
  private handleEditorChange(field: SettingsMessageEditorOpenedMetadata['field'], value: string): void {
    if (!this._editorEngagementTracked) {
      this._editorEngagementTracked = true;
      this.trackService.trackWorkflow(CollectionsSettingsStep.MESSAGE_EDITOR_OPENED, { field });
    }
    if (field === 'subject') this.subjectChange.emit(value);
    else if (field === 'headline') this.headlineChange.emit(value);
    else this.messageChange.emit(value);
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
